import type React from "react";
import { useParams, Link } from "react-router-dom";
import { useGoal } from "@/hooks/useGoal";
import GoalProgress from "./components/GoalProgress";
import ProgressForm from "./components/ProgressForm";
import { formatDate } from "@/utils/date";

const GoalDetailPage: React.FC = () => {
	const { goalId } = useParams<{ goalId: string }>();
	const { goal, loading, error } = useGoal(goalId);

	if (loading) return <p>読み込み中...</p>;
	if (error) return <p>エラーが発生しました: {error.message}</p>;
	if (!goal) return <p>目標が見つかりません</p>;

	// カテゴリーの日本語表示
	const categoryLabel = {
		short: "短期",
		medium: "中期",
		long: "長期",
	}[goal.category];

	// 優先度の日本語表示
	const priorityLabel = {
		low: "低",
		medium: "中",
		high: "高",
	}[goal.priority];

	return (
		<div className="container mx-auto px-4 py-8">
			<div className="flex justify-between items-center mb-6">
				<h1 className="text-3xl font-bold">目標詳細</h1>
				<Link
					to={`/webapp/goals/${goalId}/edit`}
					className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
				>
					編集
				</Link>
			</div>

			<div className="bg-white p-4 rounded shadow mb-6">
				<h2 className="text-2xl font-semibold mb-4">{goal.title}</h2>
				{goal.description && (
					<p className="text-gray-600 mb-4">{goal.description}</p>
				)}
				<div className="grid grid-cols-2 gap-4">
					<p className="text-sm text-gray-500">カテゴリー: {categoryLabel}</p>
					<p className="text-sm text-gray-500">優先度: {priorityLabel}</p>
					<p className="text-sm text-gray-500">
						開始日: {formatDate(goal.startDate)}
					</p>
					<p className="text-sm text-gray-500">
						終了日: {formatDate(goal.endDate)}
					</p>
					<p className="text-sm text-gray-500">
						ステータス:{" "}
						{goal.status === "completed"
							? "完了"
							: goal.status === "in_progress"
								? "進行中"
								: "未着手"}
					</p>
				</div>
			</div>

			<div className="bg-white p-4 rounded shadow mb-6">
				<h2 className="text-xl font-semibold mb-4">進捗状況</h2>
				<GoalProgress goal={goal} />
			</div>

			<div className="bg-white p-4 rounded shadow">
				<h2 className="text-xl font-semibold mb-4">進捗を記録</h2>
				<ProgressForm goalId={goalId ?? undefined} />
			</div>
		</div>
	);
};

export default GoalDetailPage;
